import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import transactionsAPI from '../../utils/api/transactions';

export const fetchTransactions = createAsyncThunk(
  'transactions/fetchTransactions',
  async (params, { rejectWithValue }) => {
    try {
      const response = await transactionsAPI.getAll(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createTransaction = createAsyncThunk(
  'transactions/createTransaction',
  async (transactionData, { rejectWithValue }) => {
    try {
      const response = await transactionsAPI.create(transactionData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// + ADD
export const createTransactionsBulk = createAsyncThunk(
  'transactions/createTransactionsBulk',
  async (payload, { rejectWithValue }) => {
    try {
      const body = Array.isArray(payload)
        ? { transactions: payload }
        : {
            transactions: payload.transactions,
            use_extern_number: payload.use_extern_number,
          };
      const result = await transactionsAPI.create(body);
      return result;
    } catch (error) {
      return rejectWithValue(error?.response?.data || { message: 'Bulk create failed' });
    }
  }
);

export const exportMasavFile = createAsyncThunk(
  'transactions/exportMasavFile',
  async ({ asc_nums, charge_date, use_extern_number }, { rejectWithValue }) => {
    try {
      const blob = await transactionsAPI.exportMasav({ asc_nums, charge_date, use_extern_number });
      return blob;
    } catch (error) {
      return rejectWithValue(error?.response?.data || { message: 'MASAV export failed' });
    }
  }
);

export const markTransactionsExported = createAsyncThunk(
  'transactions/markTransactionsExported',
  async ({ asc_nums }, { rejectWithValue }) => {
    try {
      return await transactionsAPI.markExported({ asc_nums });
    } catch (error) {
      return rejectWithValue(error?.response?.data || { message: 'Mark exported failed' });
    }
  }
);


// export const createTransactionsBulk00 = createAsyncThunk(
//   'transactions/createTransactionsBulk',
//   async (transactionsArray, { rejectWithValue }) => {
//     try {
//       // You can send either an array or { transactions: [...] }.
//       // The server supports both; we’ll send { transactions: [...] } for clarity.
//       const response = await transactionsAPI.create({ transactions: transactionsArray });
//       return response; // { success, data: [ ...createdTransactions ] }
//     } catch (error) {
//       return rejectWithValue(error?.response?.data || { message: 'Bulk create failed' });
//     }
//   }
// );

export const updateTransaction = createAsyncThunk(
  'transactions/updateTransaction',
  async ({ id, transactionData }, { rejectWithValue }) => {
    try {
      const response = await transactionsAPI.update(id, transactionData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteTransaction = createAsyncThunk(
  'transactions/deleteTransaction',
  async (id, { rejectWithValue }) => {
    try {
      await transactionsAPI.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const initialState = {
  transactionsList: [],
  loading: false,
  error: null
};

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setTransactions: (state, action) => {
      state.transactionsList = action.payload.data;
    },
    clearTransactionsState: (state) => {
      state.transactionsList = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Transactions
      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactionsList = action.payload.data;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Transaction
      .addCase(createTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTransaction.fulfilled, (state, action) => {
        state.loading = false;
        state.transactionsList.push(action.payload.data);
      })
      .addCase(createTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Transaction
      .addCase(updateTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransaction.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.transactionsList.findIndex(transaction => transaction.id === action.payload.id);
        if (index !== -1) {
          state.transactionsList[index] = action.payload.data;
        }
      })
      .addCase(updateTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Transaction
      .addCase(deleteTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTransaction.fulfilled, (state, action) => {
        state.loading = false;
        state.transactionsList = state.transactionsList.filter(transaction => transaction.id !== action.payload.data.id);
      })
      .addCase(deleteTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // + ADD into builder chain
      .addCase(createTransactionsBulk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTransactionsBulk.fulfilled, (state) => {
        state.loading = false;
        // caller refetches the full list right after, so nothing to merge here
      })
      .addCase(createTransactionsBulk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(markTransactionsExported.fulfilled, (state, action) => {
        const ascNums = new Set((action.payload?.asc_nums || []).map(String));
        if (!Array.isArray(state.transactionsList) || !ascNums.size) return;
        state.transactionsList = state.transactionsList.map((trx) =>
          ascNums.has(String(trx.asc_num)) ? { ...trx, exported: true } : trx
        );
      });

  }
});

export const { clearError, setTransactions, clearTransactionsState } = transactionsSlice.actions;
export default transactionsSlice.reducer; 